import queries from '@apis/queries';
import Column from '@components/Column';
import Image from '@components/Image';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import usePriceUnit from '@hooks/item/usePriceUnit';
import useGlobalName from '@hooks/useGlobalName';
import { OrderLine as OrderItemProps } from '@models/order';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

interface Props {
    item: OrderItemProps;
}

function OrderItem({ item }:Props) {
    const {
        itemName, quantity, personalOptions, defaultOptions, itemImageUrl, totalAmount, shopItemId,
    } = item;
    const { t } = useTranslation('order');
    const { data: itemDetail } = useQuery(queries.items.detail(shopItemId));
    const options = [...personalOptions, ...defaultOptions]
        .map((optionGroup) => optionGroup.options)
        .flat();
    const optionNames = options
        .map((option) => `${option.optionName}(${option.quantity}${t('items.optionCount')})`)
        .sort().join(' | ');
    const priceUnit = usePriceUnit();
    const globalItemName = useGlobalName({
        name: itemName,
        nameJa: itemDetail?.nameJa,
        nameEn: itemDetail?.nameEn,
    });
    return (<Container spacing={16}
        fullWidth>
        <ImageWrapper>
            <Image src={itemImageUrl}
                width={100}
                height={100}
                alt={t('items.imageAlt')} />
        </ImageWrapper>
        <TextColumn spacing={2} fullWidth>
            <NameText size='lg' fontWeight='bold' color={colors.gray6}>
                {globalItemName}
            </NameText>
            <DotWrapper spacing={8}>
                <Text>{quantity}{t('items.optionCount')}</Text>
                <Dot />
                <Text>{totalAmount.toLocaleString()}{priceUnit}</Text>
            </DotWrapper>
            <Text>
                {optionNames}
            </Text>
        </TextColumn>
    </Container>
    );
}

const Container = styled(Row)`
    border-bottom: 1px solid var(--color-gray3);
    padding: 24px 0;
    overflow: hidden;

    &:nth-last-of-type(1) {
        border-bottom: none;
    }
`;

const ImageWrapper = styled.div`
    align-items: center;
    border: 1px solid var(--color-gray2);
    border-radius: 12px;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.14);
    display: flex;
    height: 100px;
    justify-content: center;
    min-width: 100px;
    width: 100px;
    overflow: hidden;
`;

const Dot = styled.div`
    width: 5px;
    height: 5px;
    border-radius: 3px;
    background-color: var(--color-gray4);
`;

const DotWrapper = styled(Row)`
    margin-bottom: 10px;
`;

const TextColumn = styled(Column)`
    flex: 1;
    overflow: hidden;
`;

const NameText = styled(Text)`
    white-space: pre-wrap;
    word-break: break-all;
`;

export default OrderItem;
